// A recursive groupBy function that preserves item types and returns a nested object structure based on the keys.
export function groupBy<
  T,
  KArr extends readonly (keyof T)[]
>(
  items: T[],
  keys: KArr,
  options: { includeBlankValues: boolean } = { includeBlankValues: false }
): Grouped<T, KArr> {
  if (keys.length === 0) {
    return items as Grouped<T, KArr>;
  }
  const [currentKey, ...restKeys] = keys;
  const map: Record<string, Grouped<T, Tail<KArr>>> = {};

  for (const item of items) {
    const keyValue = item[currentKey!];
    let mapKey = String(keyValue);
    if (!!options.includeBlankValues && !keyValue) {
      mapKey = "(blank)";
    }

    if (!Object.prototype.hasOwnProperty.call(map, mapKey)) {
      map[mapKey] = groupBy(
        items.filter(i => i[currentKey!] === keyValue),
        restKeys as Tail<KArr>
      );
    }
  }

  return map as unknown as Grouped<T, KArr>;
}

/**
 * Helper type to extract the value type from a key
 */
type ValueOfKey<T, K extends keyof T> = T[K] extends string | number | symbol ? T[K] : string;

/**
 * Helper type to extract the tail of a tuple
 */
type Tail<T extends readonly unknown[]> = T extends readonly [any, ...infer Rest] ? Rest : [];

/**
 * Recursively constructs a type for nested grouping.
 */
type Grouped<T, KArr extends readonly (keyof T)[]> =
  KArr extends readonly []
    ? T[]
    : KArr extends readonly [infer KHead, ...infer KRest]
    ? KHead extends keyof T
      ? KRest extends readonly (keyof T)[]
        ? Record<ValueOfKey<T, KHead>, Grouped<T, KRest>>
        : never
      : never
    : T[];


